// ==========================================================================
// Générateur de documents imprimables (facture détaillée / relevé de prestations)
// Mise en page inspirée des factures Bill4Time (KD-26012) + en-tête KDLEX (Billit).
// Ouvre une fenêtre d'impression -> l'utilisatrice choisit « Enregistrer en PDF ».
// ==========================================================================
import { KDLEX_LOGO } from './kdlexLogo';

export const DEFAULT_ISSUER = {
  name: 'KDLEX',
  logo: KDLEX_LOGO,
  vat: 'ESY6383213Z',
  email: 'info@kdlex.com',
  phone: '+34 617 336 575',
  address_lines: ['Av. Perf. Palacio de la Fuente 1', '03003 Alicante', 'Espagne'],
  iban: 'ES25 2100 8730 8802 0032 3901',
  bic: 'CAIXESBB',
  footer_note: "La présente est soumise à nos conditions générales et particulières d'intervention, jointes en annexe et, le cas échéant, aux conditions particulières convenues.",
};

const ISSUER_KEY = 'orga_issuer';

export function getIssuer() {
  try {
    const raw = localStorage.getItem(ISSUER_KEY);
    if (!raw) return { ...DEFAULT_ISSUER };
    const parsed = JSON.parse(raw);
    const merged = { ...DEFAULT_ISSUER, ...parsed };
    // Sécurité : ne jamais laisser une ancienne référence de réseau traîner dans le pied de page
    const _legacyNet = new RegExp(atob('bGV4bWluZA=='), 'i');
    if (merged.footer_note && _legacyNet.test(merged.footer_note)) {
      merged.footer_note = DEFAULT_ISSUER.footer_note;
    }
    return merged;
  } catch {
    return { ...DEFAULT_ISSUER };
  }
}

export function saveIssuer(issuer) {
  localStorage.setItem(ISSUER_KEY, JSON.stringify(issuer));
}

// ---------- Helpers de formatage ----------
function money(n) {
  return Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function hours(n) {
  return Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(d) {
  if (!d) return '';
  const [y, m, day] = String(d).split('T')[0].split('-');
  if (!day) return d;
  return `${day}/${m}/${y}`;
}
function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function nl2br(s) {
  return esc(s).replace(/\n/g, '<br>');
}

// ---------- Construction du document ----------
// options : {
//   kind: 'facture' | 'releve',
//   prestations: [ ...lignes ],
//   client: { name, address, vat_number, country } | null,
//   issuer: { ... },
//   meta: { number, date, dueDate, serviceThrough, from, to, clientNumber, subject, tvaRate }
// }
export function buildDocumentHTML(options) {
  const { kind = 'releve', prestations = [], client = null, meta = {}, dossierNumbers = {}, simpleLines = [] } = options;
  const issuer = options.issuer || getIssuer();
  const isFacture = kind === 'facture';
  const docType = isFacture ? (meta.docType || 'facture') : 'releve';
  const vatRegime = meta.vatRegime || null;
  const useSimple = Array.isArray(simpleLines) && simpleLines.length > 0 && prestations.length === 0;
  const DOC = ({
    facture:            { h1: 'Facture',                numLabel: 'Facture n°',   payable: true,  proforma: false },
    proforma:           { h1: 'Facture proforma',       numLabel: 'Proforma n°',  payable: false, proforma: true  },
    provision:          { h1: 'Provision sur honoraires', numLabel: 'Provision n°', payable: true,  proforma: false },
    proforma_provision: { h1: 'Proforma — Provision',   numLabel: 'Proforma n°',  payable: false, proforma: true  },
    releve:             { h1: 'Relevé',                  numLabel: null,           payable: false, proforma: false },
  })[docType] || { h1: 'Facture', numLabel: 'Facture n°', payable: true, proforma: false };
  const dossierLabel = (name) => {
    const n = dossierNumbers[name];
    return n ? `Dossier n° ${esc(n)} — ${esc(name)}` : `Dossier : ${esc(name)}`;
  };

  // Regroupement par dossier
  const map = {};
  prestations.forEach(p => {
    const key = p.dossier || '(Sans dossier)';
    if (!map[key]) map[key] = { honoraires: [], frais: [] };
    if (p.kind === 'frais') map[key].frais.push(p);
    else map[key].honoraires.push(p);
  });
  const dossierKeys = Object.keys(map).sort((a, b) => {
    if (a.startsWith('(Sans')) return 1;
    if (b.startsWith('(Sans')) return -1;
    return a.localeCompare(b, 'fr');
  });

  // Totaux
  const simpleTotal = (simpleLines || []).reduce((s, l) => s + parseFloat(l.amount || 0), 0);
  const totalHonoraires = prestations.filter(p => p.kind === 'temps').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const totalFrais = prestations.filter(p => p.kind === 'frais').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const totalHT = useSimple ? simpleTotal : (totalHonoraires + totalFrais);
  const tvaRate = meta.tvaRate != null ? parseFloat(meta.tvaRate) : 21;
  const tvaAmount = +(totalHT * tvaRate / 100).toFixed(2);
  const totalTTC = +(totalHT + tvaAmount).toFixed(2);

  // Sections par dossier
  const sections = dossierKeys.map(dk => {
    const g = map[dk];
    let html = '';

    if (g.honoraires.length > 0) {
      const rows = g.honoraires.map(p => `
        <tr>
          <td class="c-date">${fmtDate(p.presta_date)}</td>
          <td>${p.activity ? '<strong>' + esc(p.activity) + '</strong> : ' : ''}${nl2br(p.description || '')}</td>
          <td class="c-num">${hours(p.hours)}</td>
          <td class="c-num">${money(p.amount)}</td>
        </tr>`).join('');
      const subH = g.honoraires.reduce((s, p) => s + parseFloat(p.hours || 0), 0);
      const subA = g.honoraires.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
      html += `
        <div class="ref">${dossierLabel(dk)} <span class="ref-tag">Honoraires</span></div>
        <table class="lines">
          <thead><tr><th class="c-date">Date</th><th>Prestation</th><th class="c-num">Heures</th><th class="c-num">Montant HT</th></tr></thead>
          <tbody>${rows}</tbody>
          <tfoot><tr><td colspan="2">Sous-total honoraires</td><td class="c-num">${hours(subH)}</td><td class="c-num">${money(subA)}</td></tr></tfoot>
        </table>`;
    }

    if (g.frais.length > 0) {
      const rows = g.frais.map(p => `
        <tr>
          <td class="c-date">${fmtDate(p.presta_date)}</td>
          <td>${nl2br(p.description || 'Frais')}</td>
          <td class="c-num">${money(p.amount)}</td>
        </tr>`).join('');
      const subF = g.frais.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
      html += `
        <div class="ref">${dossierLabel(dk)} <span class="ref-tag frais">Frais &amp; débours</span></div>
        <table class="lines">
          <thead><tr><th class="c-date">Date</th><th>Frais</th><th class="c-num">Montant HT</th></tr></thead>
          <tbody>${rows}</tbody>
          <tfoot><tr><td colspan="1">Sous-total frais</td><td></td><td class="c-num">${money(subF)}</td></tr></tfoot>
        </table>`;
    }
    return html;
  }).join('');

  // Ligne simple (provision / facture manuelle sans prestations détaillées)
  const simpleHtml = useSimple ? `
    <table class="lines">
      <thead><tr><th>Désignation</th><th class="c-num">Montant HT</th></tr></thead>
      <tbody>${(simpleLines || []).map(l => `
        <tr><td>${nl2br(l.description || 'Provision sur honoraires')}</td><td class="c-num">${money(l.amount)}</td></tr>`).join('')}</tbody>
    </table>` : '';

  // Bloc métadonnées
  const metaRows = [];
  if (isFacture) {
    metaRows.push([DOC.numLabel, esc(meta.number || '—')]);
    metaRows.push(['Date', fmtDate(meta.date) || '—']);
    if (meta.dueDate && DOC.payable) metaRows.push(['Échéance', fmtDate(meta.dueDate)]);
  } else {
    metaRows.push(['Document', 'Relevé de prestations']);
    if (meta.from || meta.to) metaRows.push(['Période', `${meta.from ? fmtDate(meta.from) : '…'} au ${meta.to ? fmtDate(meta.to) : '…'}`]);
    metaRows.push(['Édité le', fmtDate(meta.date) || fmtDate(new Date().toLocaleDateString('en-CA'))]);
  }
  if (meta.serviceThrough) metaRows.push(['Prestations jusqu\'au', fmtDate(meta.serviceThrough)]);
  const clientNum = meta.clientNumber || (client && client.client_number);
  if (clientNum) metaRows.push(['Client n°', esc(clientNum)]);
  if (client && client.vat_number) metaRows.push(['TVA client', esc(client.vat_number)]);
  const metaHtml = metaRows.map(([k, v]) => `<tr><td class="k">${k}</td><td class="v">${v}</td></tr>`).join('');

  // Bloc client
  const clientHtml = client ? `
    <div class="party client">
      <div class="party-title">Facturé à</div>
      <div class="party-name">${esc(client.name)}</div>
      ${client.client_number ? `<div>Client n° ${esc(client.client_number)}</div>` : ''}
      ${client.address ? `<div>${nl2br(client.address)}</div>` : ''}
      ${client.vat_number ? `<div>TVA : ${esc(client.vat_number)}</div>` : ''}
    </div>` : '';

  // Émetteur
  const issuerHtml = `
    <div class="issuer">
      ${issuer.logo
        ? `<img src="${issuer.logo}" alt="${esc(issuer.name)}" class="issuer-logo" />`
        : `<div class="issuer-name">${esc(issuer.name)}</div>`}
      ${issuer.vat ? `<div>TVA : ${esc(issuer.vat)}</div>` : ''}
      ${(issuer.address_lines || []).map(l => `<div>${esc(l)}</div>`).join('')}
      ${issuer.phone ? `<div>${esc(issuer.phone)}</div>` : ''}
      ${issuer.email ? `<div>${esc(issuer.email)}</div>` : ''}
    </div>`;

  // Totaux
  const tvaLabel = tvaRate === 0 ? 'TVA (0 % — autoliquidation intracom)' : `TVA (${hours(tvaRate).replace(',00', '')} %)`;
  const splitRows = useSimple ? '' : `
      <tr><td>Total honoraires HT</td><td class="c-num">${money(totalHonoraires)}</td></tr>
      <tr><td>Total frais &amp; débours HT</td><td class="c-num">${money(totalFrais)}</td></tr>`;
  const totalsHtml = `
    <table class="totals">
      ${splitRows}
      <tr class="sep"><td>Total HT</td><td class="c-num">${money(totalHT)}</td></tr>
      <tr><td>${tvaLabel}</td><td class="c-num">${money(tvaAmount)}</td></tr>
      <tr class="grand"><td>${DOC.payable ? 'TOTAL À PAYER (TTC)' : 'TOTAL TTC'}</td><td class="c-num">${money(totalTTC)}</td></tr>
    </table>`;

  // Mention légale TVA intracom (autoliquidation)
  const vatMention = (vatRegime === 'intracom_0' || tvaRate === 0) ? `
    <div class="vat-note">TVA non applicable — autoliquidation (inversion du sujet passif), art. 196 de la Directive 2006/112/CE.</div>` : '';

  // Bandeau proforma (document non comptable)
  const proformaBanner = DOC.proforma ? `
    <div class="proforma-banner">DOCUMENT NON COMPTABLE — NE VAUT PAS FACTURE</div>` : '';

  // Pied de paiement (documents payables : facture, provision)
  const payHtml = DOC.payable ? `
    <div class="pay">
      <div class="pay-line"><strong>À PAYER : ${money(totalTTC)}</strong>${(meta.dueDate ? ` avant le ${fmtDate(meta.dueDate)}` : '')}</div>
      <div>À l'ordre de <strong>${esc(issuer.name)}</strong>${issuer.iban ? ` — IBAN : ${esc(issuer.iban)}` : ''}${issuer.bic ? ` — BIC : ${esc(issuer.bic)}` : ''}</div>
    </div>` : '';

  const footNote = issuer.footer_note ? `<div class="foot-note">${esc(issuer.footer_note)}</div>` : '';

  const docTitle = isFacture ? `${DOC.h1} ${esc(meta.number || '')}`.trim() : 'Relevé de prestations';

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8">
    <title>${docTitle}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: 'Segoe UI', Arial, Helvetica, sans-serif; color: #1e293b; margin: 0; padding: 18mm 16mm; font-size: 12px; line-height: 1.45; }
      .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; margin-bottom: 20px; }
      .issuer-name { font-size: 22px; font-weight: 800; letter-spacing: 1px; color: #0f172a; margin-bottom: 4px; }
      .issuer-logo { max-height: 120px; max-width: 320px; margin-bottom: 10px; display: block; }
      .watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 0; opacity: 0.06; pointer-events: none; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .watermark img { width: 460px; max-width: 70vw; height: auto; }
      .content { position: relative; z-index: 1; }
      .issuer div { font-size: 11px; color: #475569; }
      .doc-title { text-align: right; }
      .doc-title h1 { font-size: 20px; margin: 0 0 8px; color: #0f172a; text-transform: uppercase; letter-spacing: 0.5px; }
      table.meta { border-collapse: collapse; margin-left: auto; }
      table.meta td { padding: 2px 6px; font-size: 11px; }
      table.meta td.k { color: #64748b; text-align: right; }
      table.meta td.v { font-weight: 700; text-align: right; }
      .party { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; background: #f8fafc; max-width: 320px; margin: 8px 0 22px auto; }
      .party-title { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; font-weight: 700; margin-bottom: 4px; }
      .party-name { font-weight: 700; font-size: 13px; }
      .ref { font-weight: 700; font-size: 12px; margin: 16px 0 4px; color: #0f172a; }
      .ref-tag { display: inline-block; font-size: 10px; font-weight: 700; background: #eff6ff; color: #2563eb; padding: 1px 8px; border-radius: 10px; margin-left: 6px; text-transform: uppercase; letter-spacing: 0.5px; }
      .ref-tag.frais { background: #fff7ed; color: #ea580c; }
      table.lines { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
      table.lines th { text-align: left; border-bottom: 1.5px solid #334155; padding: 5px 6px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px; color: #475569; }
      table.lines td { padding: 4px 6px; border-bottom: 1px solid #eef2f6; vertical-align: top; }
      table.lines tfoot td { font-weight: 700; border-top: 1px solid #94a3b8; border-bottom: none; background: #f8fafc; }
      .c-date { white-space: nowrap; width: 78px; }
      .c-num { text-align: right; white-space: nowrap; width: 90px; }
      table.totals { margin: 18px 0 0 auto; border-collapse: collapse; min-width: 300px; }
      table.totals td { padding: 4px 10px; }
      table.totals td.c-num { text-align: right; font-variant-numeric: tabular-nums; }
      table.totals tr.sep td { border-top: 1px solid #cbd5e1; font-weight: 700; }
      table.totals tr.grand td { border-top: 2px solid #0f172a; font-size: 15px; font-weight: 800; color: #0f172a; padding-top: 8px; }
      .proforma-banner { margin: 6px 0 14px; padding: 8px 12px; border: 1.5px dashed #b45309; background: #fffbeb; color: #b45309; font-weight: 800; font-size: 12px; letter-spacing: 1px; text-align: center; border-radius: 6px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .vat-note { margin: 10px 0 0 auto; max-width: 340px; font-size: 10px; color: #64748b; font-style: italic; text-align: right; }
      .pay { margin-top: 22px; padding: 12px 14px; border: 1.5px solid #0f172a; border-radius: 8px; background: #f8fafc; }
      .pay-line { font-size: 14px; margin-bottom: 4px; }
      .foot-note { margin-top: 20px; font-size: 9.5px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 8px; }
      @media print { body { padding: 12mm; } .party { background: #f8fafc !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    </style></head><body>
    ${issuer.logo ? `<div class="watermark"><img src="${issuer.logo}" alt="" /></div>` : ''}
    <div class="content">
    <div class="head">
      ${issuerHtml}
      <div class="doc-title">
        <h1>${esc(DOC.h1)}</h1>
        <table class="meta">${metaHtml}</table>
      </div>
    </div>
    ${proformaBanner}
    ${clientHtml}
    ${meta.subject ? `<div style="margin-bottom:12px;color:#475569;">Objet : ${esc(meta.subject)}</div>` : ''}
    ${useSimple ? simpleHtml : (sections || '<p style="color:#94a3b8">Aucune prestation.</p>')}
    ${totalsHtml}
    ${vatMention}
    ${payHtml}
    ${footNote}
    </div>
    <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 150); }</script>
    </body></html>`;
}

// Ouvre le document dans une nouvelle fenêtre et lance l'impression
export function openPrestationDocument(options) {
  const html = buildDocumentHTML(options);
  const w = window.open('', '_blank');
  if (!w) {
    alert("La fenêtre d'impression a été bloquée par le navigateur. Autorisez les pop-ups pour ce site.");
    return false;
  }
  w.document.write(html);
  w.document.close();
  return true;
}
